import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  AddFormButton,
  FilterButton,
} from "../../../shared/components/TableElements";
import { Desc, LinkSpan } from "../../../shared/components/form/FormElements";
import {
  colorLightGray,
  colorTableBorder,
  colorTextAdditional,
  colorBlueHover,
  colorLightText,
  colorStatusActive,
} from "../../../utils/palette";
import { useStripe } from "@stripe/react-stripe-js";
import { postApi } from "../../../api/getUser";
import { getDeclineErrorMessage } from "../../../shared/helpers";
import { formatNumber, handleNegative } from "../../../shared/helpers";
import { NumInput } from "../NewPlan";
import { ReactComponent as EquipmentIcon } from "../../../shared/img/sidebar/barrow.svg";
import { EstimatedBox } from "./WexAddonRow";
import CancelAddonModal from "./CancelAddonModal";
import ReactivateAddonModal from "./ReactivateAddonModal";
import ConfirmAddonModal from "./ConfirmAddonModal";
import { Wrapper } from "./TelematicsAddonRow";

const STATUS = {
  LOCKED: "locked",
  ELIGIBLE: "inactive",
  ACTIVE: "active",
  CANCELLING: "canceled",
};

export const NumberInput = ({ value, setValue }) => {
  const handlePlusMinus = (operation) => {
    if (operation === "+") {
      if (+value < 10) {
        setValue(10 + 1);
      } else {
        setValue(+value + 1);
      }
    } else if (operation === "-") {
      if (+value <= 10) {
        setValue(10);
      } else {
        setValue(+value - 1);
      }
    }
  };
  return (
    <div className="d-flex">
      <FilterButton
        style={{ minWidth: "min-content", borderRadius: "5px 0 0 5px" }}
        onClick={() => handlePlusMinus("-")}
        //className="d-flex justify-content-center align-items-center"
      >
        <i className="fi fi-rr-minus-small m-0 mt-1"></i>
      </FilterButton>
      <NumInput
        type="number"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onWheel={(e) => e.target.blur()}
        min={10}
        onKeyDown={handleNegative}
        onBlur={() => {
          if (!value || value < 10) {
            setValue(10);
          }
        }}
      />
      <FilterButton
        style={{ minWidth: "min-content", borderRadius: "0 5px 5px 0" }}
        onClick={() => handlePlusMinus("+")}
      >
        <i className="fi fi-rr-plus-small m-0 mt-1"></i>
      </FilterButton>
    </div>
  );
};

const EquipmentPurchase = ({
  equipmentQuantity,
  status = STATUS.LOCKED,
  isSubscriptionActive,
  isMobileSubscription,
  unitPrice,
  planName,
  isNewPlan,
  totalVehicleSubAmount,
  currency,
  inputDuration, //From Vehicle sub plan (1 for monthly, 2 for yearly)
  periodEndDate,
  totalAfterCancellation,
  wexStatus,
  wexUnitPrice,
  vehicleCount,
  telematicsUnitPrice,
  telematicsStatus,
  isNotOldAdvancedUser,
  onAddSuccess,
  onUpdateSuccess,
  onCancelSuccess,
  setShowMobileAddOnErrPopup,
  paymentData,
  currentProductId,
  onSubscribeClick,
}) => {
  const { t } = useTranslation("common");
  const stripe = useStripe();

  // const canToggle =
  //   status === STATUS.ELIGIBLE ||
  //   status === STATUS.ACTIVE ||
  //   status === STATUS.CANCELLING;
  const canToggle =
    status !== STATUS.LOCKED && (isSubscriptionActive || isMobileSubscription);

  //const canToggle = (status !== STATUS.CANCELLING && isSubscriptionActive) || status === STATUS.LOCKED;
  const [isCollapsed, setIsCollapsed] = useState(status !== STATUS.LOCKED);
  const [pieces, setPieces] = useState(equipmentQuantity || 10);
  const [loading, setLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showAddConfirm, setShowAddConfirm] = useState(false);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  const [error, setError] = useState("");

  const currencyCode = currency || "USD";

  const billingLabel = useMemo(() => {
    return inputDuration === 1 ? t("month_sm") : t("year_sm");
  }, [inputDuration, t]);

  const grandTotalLabel =
    inputDuration === 2 ? t("new_yearly_total") : t("new_monthly_total");

  const billingNote = paymentData
    ? t("confirm_changes_billing_note")
    : undefined;

  const subTitleText = useMemo(() => {
    if (status === STATUS.ACTIVE) {
      return `${equipmentQuantity} ${t("equipment_sm")} × ${currencyCode} ${formatNumber(unitPrice, 2)} ${t("per")} ${billingLabel}`;
    }
    if (status === STATUS.CANCELLING) {
      return `${t("cancels_on")} ${periodEndDate || ""}`;
    }
    return `${currencyCode} ${formatNumber(unitPrice, 2)} ${t("per")} ${t("equipment_sm")} ${t("per")} ${billingLabel}`;
  }, [
    currencyCode,
    unitPrice,
    billingLabel,
    equipmentQuantity,
    status,
    periodEndDate,
    t,
  ]);

  // useEffect(() => {
  //   if (!canToggle) {
  //     setIsCollapsed(false);
  //   }
  // }, [canToggle]);

  // const handleStep = (direction) => {
  //   if (status === STATUS.LOCKED) return;
  //   const delta = direction === "+" ? 10 : -10;
  //   const parsedValue = Number(pieces + delta);
  //   setPieces(parsedValue);
  // };

  const featureList = useMemo(
    () => [
      t("small_equipment_feature_reminders"),
      t("small_equipment_feature_log_services"),
      t("small_equipment_feature_track_issues"),
    ],
    [t],
  );

  const addOnTotal = useMemo(() => {
    const price = Number(unitPrice) * Number(pieces);
    return Number.isNaN(price) ? 0 : Number(price);
  }, [pieces, unitPrice]);

  const estimatedTotal = useMemo(() => {
    if (totalVehicleSubAmount === undefined || totalVehicleSubAmount === null)
      return null;
    const wexTotal =
      wexStatus === STATUS.ACTIVE
        ? (wexUnitPrice || 0) * (vehicleCount || 0)
        : 0;
    const telematicsTotal =
      telematicsStatus === STATUS.ACTIVE && isNotOldAdvancedUser
        ? (telematicsUnitPrice || 0) * (vehicleCount || 0)
        : 0;
    const sum = Number(totalVehicleSubAmount) + addOnTotal + wexTotal + telematicsTotal;
    return Number(sum);
  }, [
    addOnTotal,
    totalVehicleSubAmount,
    wexStatus,
    wexUnitPrice,
    vehicleCount,
    telematicsStatus,
    telematicsUnitPrice,
    isNotOldAdvancedUser,
  ]);

  const showEstimatedTotal =
    status === STATUS.ELIGIBLE && estimatedTotal !== null;

  let plansLabel = t("vehicle_plan_plus_small_equipment");
  if (wexStatus === STATUS.ACTIVE) {
    plansLabel += `  + ${t("wex_fuel_card")}`;
  }
  if (telematicsStatus === STATUS.ACTIVE && isNotOldAdvancedUser) {
    plansLabel += `  + ${t("telematics")}`;
  }

  const activeSummaryText = t("small_equipment_active_summary", {
    count: equipmentQuantity,
    currency: currencyCode,
    price: formatNumber(unitPrice, 2),
  });

  const handleEquipmentAddOnCheckout = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await postApi(
        { querystring: "add_equipment_addon", quantity: pieces },
        "subscription",
      );
      if (res.success) {
        onAddSuccess();
      } else {
        if (res?.requires_action && res?.client_secret) {
          const confirmResult = await stripe.confirmCardPayment(res.client_secret);
          if (confirmResult.paymentIntent) {
            onAddSuccess();
            return;
          } else if (confirmResult.error) {
            setError(getDeclineErrorMessage("payment_intent_authentication_failure", t, "error_adding_equipment"));
            return;
          } else {
            setError(getDeclineErrorMessage("payment_intent_authentication_failure", t, "error_adding_equipment"));
            return;
          }
        }
        setError(
          getDeclineErrorMessage(res.decline_code, t, "error_adding_equipment"),
        );
      }
    } catch {
      setError(t("error_adding_equipment"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEquipment = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await postApi(
        { querystring: "cancel_equipment_addon" },
        "subscription",
      );
      if (res.success) {
        setShowCancelConfirm(false);
        onCancelSuccess();
      } else {
        setError(t("error_cancelling_equipment"));
      }
    } catch {
      setError(t("error_cancelling_equipment"));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEquipment = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await postApi(
        { querystring: "update_equipment_count", quantity: pieces },
        "subscription",
      );
      if (res.success) {
        onUpdateSuccess(pieces);
      } else {
        if (res?.requires_action && res?.client_secret) {
          const confirmResult = await stripe.confirmCardPayment(res.client_secret);
          if (confirmResult.paymentIntent) {
            onUpdateSuccess(pieces);
            return;
          } else if (confirmResult.error) {
            setError(getDeclineErrorMessage("payment_intent_authentication_failure", t, "error_updating_equipment"));
            return;
          } else {
            setError(getDeclineErrorMessage("payment_intent_authentication_failure", t, "error_updating_equipment"));
            return;
          }
        }
        setError(
          getDeclineErrorMessage(
            res.decline_code,
            t,
            "error_updating_equipment",
          ),
        );
      }
    } catch {
      setError(t("error_updating_equipment"));
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateEquipment = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await postApi(
        { querystring: "add_equipment_addon", quantity: pieces },
        "subscription",
      );
      if (res.success) {
        setShowReactivateModal(false);
        onAddSuccess();
      } else {
        if (res?.requires_action && res?.client_secret) {
          const confirmResult = await stripe.confirmCardPayment(res.client_secret);
          if (confirmResult.paymentIntent) {
            setShowReactivateModal(false);
            onAddSuccess();
            return;
          } else if (confirmResult.error) {
            setError(getDeclineErrorMessage("payment_intent_authentication_failure", t, "error_reactivating_equipment"));
            return;
          } else {
            setError(getDeclineErrorMessage("payment_intent_authentication_failure", t, "error_reactivating_equipment"));
            return;
          }
        }
        setError(
          getDeclineErrorMessage(
            res.decline_code,
            t,
            "error_reactivating_equipment",
          ),
        );
      }
    } catch {
      setError(t("error_reactivating_equipment"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper
      data-testid="equipment-purchase"
      $status={status}
      role="region"
      aria-label={t("small_equipment_add_on")}
    >
      <Header
        role={canToggle ? "button" : "heading"}
        aria-expanded={!isCollapsed}
        tabIndex={canToggle ? 0 : -1}
        onClick={() => canToggle && setIsCollapsed((prev) => !prev)}
        onKeyDown={(event) => {
          if (!canToggle) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setIsCollapsed((prev) => !prev);
          }
        }}
      >
        <div className="d-flex gap-3 align-items-center">
          <IconBubble $status={status}>
            <EquipmentIconWrapper>
              <EquipmentIcon />
            </EquipmentIconWrapper>
          </IconBubble>
          <div>
            <div className="d-flex align-items-center gap-2">
              <Title>{t("small_equipment_add_on")}</Title>
              {status === STATUS.ACTIVE && (
                <ActiveBadge>{t("active")}</ActiveBadge>
              )}
              {status === STATUS.CANCELLING && (
                <CancellingBadge>{t("cancelling_sm")}</CancellingBadge>
              )}
            </div>
            <Subtitle $status={status}>{subTitleText}</Subtitle>
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          {(status === STATUS.ACTIVE || status === STATUS.CANCELLING) && (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {`${currencyCode} ${formatNumber(unitPrice * equipmentQuantity, 2)}/${billingLabel}`}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {t("for_n_equipment", { count: equipmentQuantity })}
              </div>
            </div>
          )}
          {canToggle && (
            <Chevron
              className={`fi fi-rr-angle-${isCollapsed ? "down" : "up"}`}
            />
          )}
        </div>
      </Header>

      {!isCollapsed && (
        <Body>
          {status === STATUS.CANCELLING ? (
            /* ── Cancelling state ── */
            <>
              <ReactivateAddonModal
                show={showReactivateModal}
                onHide={() => {
                  setError("");
                  setShowReactivateModal(false);
                }}
                title={t("reactivate_equipment_title")}
                addonName={t("small_equipment")}
                addonCost={`${currencyCode} ${formatNumber(unitPrice * equipmentQuantity, 2)}/${billingLabel}`}
                onConfirm={handleReactivateEquipment}
                loading={loading}
                error={error}
              />

              <CancellingNotice>
                <i
                  className="fi fi-rr-triangle-warning"
                  style={{
                    position: "relative",
                    top: 2,
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                />
                {t("addon_cancelling_notice", {
                  addonName: t("small_equipment_add_on"),
                  date: periodEndDate || "",
                })}
              </CancellingNotice>

              {/* {error && <ErrorText>{error}</ErrorText>} */}

              {isSubscriptionActive && (
                <div>
                  <LinkSpan onClick={() => setShowReactivateModal(true)}>
                    {t("reactivate_addon")}
                  </LinkSpan>
                </div>
              )}
            </>
          ) : status === STATUS.ACTIVE ? (
            <>
              <CancelAddonModal
                show={showCancelConfirm}
                onHide={() => {
                  setError("");
                  setShowCancelConfirm(false);
                }}
                title={t("cancel_equipment_title")}
                addonName={t("small_equipment_add_on")}
                periodEndDate={periodEndDate}
                inputDuration={inputDuration}
                addonCost={`${currencyCode} ${formatNumber(unitPrice * equipmentQuantity, 2)}/${billingLabel}`}
                totalAfterCancellation={
                  totalAfterCancellation != null
                    ? `${currencyCode} ${formatNumber(totalAfterCancellation, 2)}/${billingLabel}`
                    : null
                }
                onConfirm={handleCancelEquipment}
                loading={loading}
                error={error}
              />

              <EquipmentsContainer>
                <SectionTitle>{t("equipment_pieces_label")}</SectionTitle>
                <div className="d-flex gap-3 align-items-center">
                  <NumberInput value={pieces} setValue={setPieces} />
                  <div>
                    <EstimatedValue>
                      {`${currencyCode} ${formatNumber(addOnTotal, 2)}/${billingLabel}`}
                    </EstimatedValue>
                    <Desc style={{ textAlign: "right" }}>
                      {t("price_for_selected_pieces", { count: pieces })}
                    </Desc>
                  </div>
                </div>
              </EquipmentsContainer>

              {/* {error && <ErrorText>{error}</ErrorText>} */}

              <Footer style={{ justifyContent: "space-between" }}>
                <LinkSpan
                  style={{ fontSize: 13, color: "#9ca3af" }}
                  onClick={() => setShowCancelConfirm(true)}
                >
                  {t("cancel_addon")}
                </LinkSpan>
                <AddFormButton
                  type="button"
                  onClick={() => {
                    if (isMobileSubscription) {
                      setShowMobileAddOnErrPopup(true);
                      return;
                    }
                    setShowUpdateConfirm(true);
                  }}
                  disabled={
                    !isSubscriptionActive || pieces === equipmentQuantity
                  }
                >
                  {t("update")}
                </AddFormButton>
              </Footer>

              <ConfirmAddonModal
                show={showUpdateConfirm}
                onHide={() => {
                  setError("");
                  setShowUpdateConfirm(false);
                }}
                grandTotalLabel={grandTotalLabel}
                billingNote={billingNote}
                paymentData={paymentData}
                lineItems={[
                  ...(totalVehicleSubAmount != null
                    ? [
                        {
                          name: planName || "",
                          detail: vehicleCount
                            ? `${vehicleCount} ${t("vehicles_sm")} × ${currencyCode} ${formatNumber(totalVehicleSubAmount / vehicleCount, 2)}`
                            : undefined,
                          amount: `${currencyCode} ${formatNumber(totalVehicleSubAmount, 2)}/${billingLabel}`,
                        },
                      ]
                    : []),
                  ...(wexStatus === STATUS.ACTIVE
                    ? [
                        {
                          name: t("wex_fuel_card"),
                          detail: `${vehicleCount} ${t("vehicles_sm")} × ${currencyCode} ${formatNumber(wexUnitPrice || 0, 2)}`,
                          amount: `${currencyCode} ${formatNumber((wexUnitPrice || 0) * (vehicleCount || 0), 2)}/${billingLabel}`,
                        },
                      ]
                    : []),
                  ...(telematicsStatus === STATUS.ACTIVE && isNotOldAdvancedUser
                    ? [
                        {
                          name: t("telematics"),
                          detail: `${vehicleCount} ${t("vehicles_sm")} × ${currencyCode} ${formatNumber(telematicsUnitPrice || 0, 2)}`,
                          amount: `${currencyCode} ${formatNumber((telematicsUnitPrice || 0) * (vehicleCount || 0), 2)}/${billingLabel}`,
                        },
                      ]
                    : []),
                  {
                    name: t("small_equipment_add_on"),
                    detail: `${pieces} ${t("equipment_sm")} × ${currencyCode} ${formatNumber(unitPrice, 2)}`,
                    amount: `${currencyCode} ${formatNumber(addOnTotal, 2)}/${billingLabel}`,
                  },
                ]}
                grandTotalAmount={(() => {
                  if (totalVehicleSubAmount == null) return undefined;
                  const wexTotal =
                    wexStatus === STATUS.ACTIVE
                      ? (wexUnitPrice || 0) * (vehicleCount || 0)
                      : 0;
                  const telematicsTotal =
                    telematicsStatus === STATUS.ACTIVE && isNotOldAdvancedUser
                      ? (telematicsUnitPrice || 0) * (vehicleCount || 0)
                      : 0;
                  return `${currencyCode} ${formatNumber(totalVehicleSubAmount + wexTotal + telematicsTotal + addOnTotal, 2)}/${billingLabel}`;
                })()}
                onConfirm={() => {
                  //setShowUpdateConfirm(false);
                  handleUpdateEquipment();
                }}
                loading={loading}
                error={error}
                previewParams={{
                  new_product_id: Number(currentProductId),
                  duration: inputDuration,
                  quantity: vehicleCount,
                  equipment_quantity: pieces,
                  add_telematics: false,
                  add_fuel_card: false
                }}
              />
            </>
          ) : (
            <>
              <Section>
                <SectionTitle>
                  {t("small_equipment_capabilities_title")}
                </SectionTitle>
                <FeatureList>
                  {featureList.map((feature) => (
                    <FeatureItem key={feature}>
                      <i className="fi fi-rs-check-circle" />
                      <span>{feature}</span>
                    </FeatureItem>
                  ))}
                </FeatureList>
              </Section>

              <EquipmentsContainer style={{ marginTop: "auto" }}>
                {/* <div> */}
                <p style={{fontSize: 14}}>
                  {t("no_of_equipment")}
                </p>
                {/* <QuantityCard>
                    <QuantityIconButton
                      type="button"
                      onClick={() => handleStep("-")}
                      disabled={status === STATUS.LOCKED || pieces <= 10}
                      aria-label={t("decrease")}
                    >
                      <i className="fi fi-rr-minus" />
                    </QuantityIconButton>
                    <QuantityDisplay>
                      <QuantityValue>{pieces}</QuantityValue>
                      <QuantityLabel>{t("pieces_label")}</QuantityLabel>
                    </QuantityDisplay>
                    <QuantityIconButton
                      type="button"
                      onClick={() => handleStep("+")}
                      disabled={status === STATUS.LOCKED}
                      aria-label={t("increase")}
                    >
                      <i className="fi fi-rr-plus" />
                    </QuantityIconButton>
                  </QuantityCard> */}
                {/* </div> */}
                <div className="d-flex flex-column gap-2">
                  <NumberInput value={pieces} setValue={setPieces} />
                  <div>
                    <EstimatedValue>
                      {`${currencyCode} ${formatNumber(
                        addOnTotal,
                        2,
                      )}/${billingLabel}`}
                    </EstimatedValue>
                    <Desc>
                      {t("price_for_selected_pieces", { count: pieces })}
                    </Desc>
                  </div>
                </div>
              </EquipmentsContainer>

              {/* {showEstimatedTotal && (
                <EquipmentsContainer>
                  <>
                    <div>
                      <EstimatedLabel>
                        {t("estimated_total_label")}
                      </EstimatedLabel>
                      <Desc>{t("vehicle_plan_plus_small_equipment")}</Desc>
                    </div>
                    <div>
                      <EstimatedTotal>
                        {`${currencyCode} ${formatNumber(
                          estimatedTotal,
                          2
                        )}/${billingLabel}`}
                      </EstimatedTotal>
                      <Desc style={{ textAlign: "right" }}>
                        {t("with_plan_selected", {
                          planName: planName || t("plan"),
                        })}
                      </Desc>
                    </div>
                  </>
                </EquipmentsContainer>
              )} */}

              {showEstimatedTotal && (
                <EstimatedBox>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {t("estimated_total_label")}
                    </div>
                    <Desc>{plansLabel}</Desc>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                      {`${currency} ${formatNumber(estimatedTotal, 2)}/${billingLabel}`}
                    </div>
                    <Desc style={{ textAlign: "right" }}>
                      {t("with_plan_selected", {
                        planName: planName || t("plan"),
                      })}
                    </Desc>
                  </div>
                </EstimatedBox>
              )}

              <Footer>
                {status === STATUS.LOCKED && onSubscribeClick ? (
                  <FilterButton type="button" className="w-100" onClick={onSubscribeClick} id="add-equipment-plan-btn">
                    {t("subscribe_to_add")}
                  </FilterButton>
                ) : (
                  <AddFormButton
                    type="button"
                    // onClick={
                    //   status === STATUS.ELIGIBLE
                    //     ? () => setShowAddConfirm(true)
                    //     : undefined
                    // }
                    onClick={() => {
                      if (isMobileSubscription) {
                        setShowMobileAddOnErrPopup(true);
                        return;
                      }
                      setShowAddConfirm(true);
                    }}
                    disabled={
                      loading ||
                      status === STATUS.LOCKED ||
                      (!isSubscriptionActive && !isMobileSubscription)
                    }
                    id="add-equipment-plan-btn"
                  >
                    {t("add_to_plan")}
                  </AddFormButton>
                )}
              </Footer>

              <ConfirmAddonModal
                show={showAddConfirm}
                onHide={() => {
                  setError("");
                  setShowAddConfirm(false);
                }}
                grandTotalLabel={grandTotalLabel}
                billingNote={billingNote}
                paymentData={paymentData}
                lineItems={[
                  ...(totalVehicleSubAmount != null
                    ? [
                        {
                          name: planName || "",
                          detail:
                            vehicleCount && isNewPlan
                              ? `${vehicleCount} ${t("vehicles_sm")} × ${currencyCode} ${formatNumber(totalVehicleSubAmount / vehicleCount, 2)}`
                              : undefined,
                          amount: `${currencyCode} ${formatNumber(totalVehicleSubAmount, 2)}/${billingLabel}`,
                        },
                      ]
                    : []),
                  ...(wexStatus === STATUS.ACTIVE
                    ? [
                        {
                          name: t("wex_fuel_card"),
                          detail: `${vehicleCount} ${t("vehicles_sm")} × ${currencyCode} ${formatNumber(wexUnitPrice || 0, 2)}`,
                          amount: `${currencyCode} ${formatNumber((wexUnitPrice || 0) * (vehicleCount || 0), 2)}/${billingLabel}`,
                        },
                      ]
                    : []),
                  ...(telematicsStatus === STATUS.ACTIVE && isNotOldAdvancedUser
                    ? [
                        {
                          name: t("telematics"),
                          detail: `${vehicleCount} ${t("vehicles_sm")} × ${currencyCode} ${formatNumber(telematicsUnitPrice || 0, 2)}`,
                          amount: `${currencyCode} ${formatNumber((telematicsUnitPrice || 0) * (vehicleCount || 0), 2)}/${billingLabel}`,
                        },
                      ]
                    : []),
                  {
                    name: t("small_equipment_add_on"),
                    detail: `${pieces} ${t("equipment_sm")} × ${currencyCode} ${formatNumber(unitPrice, 2)}`,
                    amount: `${currencyCode} ${formatNumber(addOnTotal, 2)}/${billingLabel}`,
                  },
                ]}
                grandTotalAmount={(() => {
                  if (totalVehicleSubAmount == null) return undefined;
                  const wexTotal =
                    wexStatus === STATUS.ACTIVE
                      ? (wexUnitPrice || 0) * (vehicleCount || 0)
                      : 0;
                  const telematicsTotal =
                    telematicsStatus === STATUS.ACTIVE && isNotOldAdvancedUser
                      ? (telematicsUnitPrice || 0) * (vehicleCount || 0)
                      : 0;
                  return `${currencyCode} ${formatNumber(totalVehicleSubAmount + wexTotal + telematicsTotal + addOnTotal, 2)}/${billingLabel}`;
                })()}
                onConfirm={() => {
                  //setShowAddConfirm(false);
                  handleEquipmentAddOnCheckout();
                }}
                loading={loading}
                error={error}
                previewParams={{
                  new_product_id: Number(currentProductId),
                  duration: inputDuration,
                  quantity: vehicleCount,
                  equipment_quantity: pieces,
                  add_telematics: false,
                  add_fuel_card: false,
                  add_equipment: true,
                }}
                isAddonPurchase
              />
              {/* {status === STATUS.LOCKED && (
                <div className="w-100 d-flex justify-content-center">
                  <HelperText>
                    <i
                      className="fi fi-rr-lock"
                      style={{ position: "relative", top: 2, marginRight: 6 }}
                    />
                    {t("subscribe_to_enable_addon")}
                  </HelperText>
                </div>
              )} */}
            </>
          )}
        </Body>
      )}
    </Wrapper>
  );
};

export default EquipmentPurchase;

// const Wrapper = styled.div`
//   border-radius: 5px;
//   padding: 0.1rem 0.75rem;
//   box-shadow: 2px 2px 2px lightgray;
//   border: 1px solid
//     ${(props) =>
//       props.$status === STATUS.ACTIVE
//         ? "#22c55e"
//         : props.$status === STATUS.CANCELLING
//           ? "#f97316"
//           : "transparent"};
//   background: ${(props) =>
//     props.$status === STATUS.LOCKED ? "#ffffff" : "#ffffff"};
//   height: 100%;
//   display: flex;
//   flex-direction: column;
// `;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  //cursor: pointer;
  &[role="button"] {
    cursor: pointer;
  }
`;

const Body = styled.div`
  padding: 0 12px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const IconBubble = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  background: ${(props) =>
    props.$status === STATUS.ACTIVE ? colorStatusActive : "#f9fafb"};
  border: 1px solid ${colorLightGray};
  color: #0f172a;
`;

const EquipmentIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: inherit;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Title = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(p) =>
    p.$status === STATUS.CANCELLING ? "red" : colorTextAdditional};
`;

const AvailabilityTag = styled.span`
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  //text-transform: uppercase;
`;

const ActiveBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #dcfce7;
  color: #166534;
  text-transform: uppercase;
`;

const CancellingBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #ffedd5;
  color: #c2410c;
  text-transform: uppercase;
`;

const CancellingNotice = styled.div`
  display: flex;
  align-items: flex-start;
  background: #fef9c3;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 13.5px;
  color: #92400e;
  line-height: 1.5;
`;

const Chevron = styled.i`
  font-size: 18px;
  color: ${colorTextAdditional};
  margin-top: 4px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h5`
  font-weight: 500;
  margin-bottom: 0;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colorLightText};
  i {
    color: #16a34a;
    position: relative;
    top: 2px;
  }
`;

const QuantityCard = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 24px;
  padding: 14px 0;
  border-radius: 12px;
  //background: #f7f7f7;
  //border: 1px solid #ededed;
`;

const QuantityIconButton = styled.button`
  //width: 40px;
  //height: 40px;
  border-radius: 10px;
  border: 1px solid #dcdcdc;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #141515;
  transition: background 0.2s ease;
  &:hover:not(:disabled) {
    background: #f0f0f0;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  i {
    font-size: 12px;
    margin: 0;
    padding: 0.25rem;
    position: relative;
    top: 2px;
  }
`;

const QuantityDisplay = styled.div`
  min-width: 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.1;
`;

const QuantityValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
`;

const QuantityLabel = styled.span`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colorTextAdditional};
`;

const PiecesHint = styled(Desc)`
  margin-top: 8px;
  color: ${colorTextAdditional};
`;

const EquipmentsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f7f7f7;
  padding: 14px 14px;
  border-radius: 8px;
`;

const PriceSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
`;

const CardBase = styled.div`
  flex: 1 1 260px;
  min-width: 260px;
  border-radius: 12px;
  background: #f7f7f7;
  padding: 20px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PriceCard = styled(CardBase)`
  border: 1px solid #ededed;
`;

const CardCopy = styled.div`
  margin-left: auto;
  text-align: right;
`;

const PriceLabel = styled.h4`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
`;

const EstimatedBlock = styled(CardBase)`
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const EstimatedLabel = styled.p`
  font-weight: 400;
  //color: ${colorTextAdditional};
`;

const EstimatedValue = styled.p`
  font-size: 16px;
  font-weight: 600;
`;

const EstimatedTotal = styled.p`
  font-size: 20px;
  font-weight: 600;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const HelperText = styled(Desc)`
  color: ${colorTextAdditional};
`;

const ActiveSection = styled.div`
  display: flex;
  //align-items: center;
  justify-content: space-between;
`;

const SummaryTitle = styled.h5`
  margin: 0;
`;

// const PaymentMethod = styled.div`
//   font-size: 14px;
//   color: ${colorTextAdditional};
// `;

const Divider = styled.span`
  width: 1px;
  height: 20px;
  background: ${colorLightGray};
  display: inline-block;
`;

const PriceBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f7f7f7;
  padding: 14px 20px;
  border-radius: 8px;
`;

const CancelConfirm = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 12px 16px;
`;

const WarningText = styled.div`
  font-size: 13px;
  color: #991b1b;
  line-height: 1.5;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #ef4444;
`;

const SecondaryBtn = styled.button`
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #4b5563;
  cursor: pointer;
  font-family: inherit;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DangerBtn = styled.button`
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  background: #ef4444;
  color: #ffffff;
  cursor: pointer;
  font-family: inherit;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
